/**
 * golden-prefill.ts — PURE, React-free derivations for the in-pane nuance
 * composer (#145 §7).
 *
 * WHY THIS EXISTS
 * A seven-field governance form is a form nobody uses. Every golden field except
 * the nuance itself is derivable from data the browser already has (the fund
 * switcher, the entitled-funds catalog, the chat turn, and the `final-sources`
 * SSE event), so the composer's default state is ONE textarea and ONE button and
 * everything else is prefilled behind a "Details" disclosure.
 *
 * Every derivation here is BEST-EFFORT and NON-BLOCKING. A failed derivation
 * leaves the field empty; it must never be able to prevent a capture. That
 * asymmetry is deliberate: a missing prefill costs the analyst one edit, a
 * blocked capture costs the firm the insight.
 *
 * REGEX PARITY: FIGURE_RE and PERFORMANCE_RE are verbatim copies of the engine's
 * definitions in `worker/src/lib/golden-governance.js`, which is the single
 * source of truth (the SERVER enforces; this copy only drives prefill hints).
 * `golden-prefill.test.ts` reads that file and asserts the sources match, so a
 * drift breaks a test rather than silently desynchronizing the UI hint from the
 * gate that will actually judge the submission.
 */

import type { ChatBinding, ChatSource } from "@/lib/api";

// ── verbatim from worker/src/lib/golden-governance.js ────────────────────────
export const FIGURE_RE =
  /[$£€]\s?\d|\d\s?%|\b\d+(?:\.\d+)?\s?(?:x|bps|bn|mm|m|k)\b|\b\d{1,3}(?:,\d{3})+\b|\b\d+\.\d+\b/i;
export const PERFORMANCE_RE =
  /\b(IRR|MOIC|TVPI|DPI|net\s+return|gross\s+return|net\s+irr|gross\s+irr|track\s+record|annualized\s+return|realized\s+return|unrealized\s+return|fund\s+performance|since\s+inception)\b/i;

/** Where a prefilled value came from — shown on the chip so the analyst can
 *  tell an INFERRED value from an OBSERVED one before they trust it. */
export type PrefillOrigin =
  | "binding"
  | "document-name"
  | "question"
  | "chat"
  | "catalog"
  | "nearest-golden"
  | "default"
  | "none";

export type Derived<T> = { value: T; origin: PrefillOrigin };

const none = <T,>(value: T): Derived<T> => ({ value, origin: "none" });

// ─── as_of ───────────────────────────────────────────────────────────────────

/**
 * Corpus filename date conventions: `… (2026.06.01)` and `… 2026.05.14`, plus
 * ISO `2026-06-30`. Deliberately narrow — a false date is worse than none,
 * because `as_of` is what makes a stale figure detectable on a future lookup.
 */
const DOC_DATE_RE = /\b(20\d{2})[.\-/](0[1-9]|1[0-2])[.\-/](0[1-9]|[12]\d|3[01])\b/g;

function normalizeDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const s = String(value).trim();
  const iso = /^(20\d{2})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.exec(s);
  if (iso) return s;
  const loose = new RegExp(DOC_DATE_RE.source).exec(s);
  if (loose) return `${loose[1]}-${loose[2]}-${loose[3]}`;
  return null;
}

/**
 * as_of derivation, in the spec's three-way fallback order:
 *   1. the most recent `as_of` across the answer's bindings;
 *   2. else the newest date parsed from the cited document NAMES;
 *   3. else blank — and blank is only required-on-submit when the answer states
 *      a figure.
 */
export function deriveAsOf(
  bindings: ChatBinding[] | undefined,
  sourceNames: string[] | undefined,
): Derived<string> {
  const fromBindings = (bindings ?? [])
    .map((b) => normalizeDate(b?.as_of))
    .filter((d): d is string => !!d)
    .sort();
  if (fromBindings.length) {
    return { value: fromBindings[fromBindings.length - 1], origin: "binding" };
  }

  const fromNames: string[] = [];
  for (const name of sourceNames ?? []) {
    const re = new RegExp(DOC_DATE_RE.source, "g");
    let m: RegExpExecArray | null;
    while ((m = re.exec(String(name ?? ""))) !== null) {
      fromNames.push(`${m[1]}-${m[2]}-${m[3]}`);
    }
  }
  if (fromNames.length) {
    fromNames.sort();
    return { value: fromNames[fromNames.length - 1], origin: "document-name" };
  }

  return none("");
}

// ─── investors ───────────────────────────────────────────────────────────────

/**
 * Legal-entity extraction from the question: capitalized spans ending in a legal
 * suffix. Conservative by design — a wrong entity chip is worse than an absent
 * one, since `investors` records who an answer was written FOR.
 */
const ENTITY_RE =
  /\b((?:[A-Z][\w&'’.-]*\s+){0,7}[A-Z][\w&'’.-]*,?\s+(?:LLC|L\.L\.C\.|L\.P\.|LP|Ltd\.?|Limited|Inc\.?|Corp\.?|N\.V\.|B\.V\.|PLC))(?![\w])/g;

/**
 * A sentence-initial capitalized function word is not part of an entity name
 * ("Does Outpost Partnership, LP hold …"). Trimming these is the difference
 * between a chip the analyst accepts and one they have to fix every time.
 */
const LEADING_STOPWORDS = new Set([
  "what", "which", "who", "whom", "whose", "does", "do", "did", "is", "are", "was",
  "were", "how", "why", "when", "where", "can", "could", "should", "would", "will",
  "the", "and", "but", "if", "in", "for", "of", "at", "on", "to", "by", "with",
  "about", "please", "tell", "explain", "confirm", "describe", "hold", "holds",
]);

export function extractInvestorEntities(question: string): Derived<string[]> {
  const out: string[] = [];
  const re = new RegExp(ENTITY_RE.source, "g");
  let m: RegExpExecArray | null;
  while ((m = re.exec(String(question ?? ""))) !== null) {
    const tokens = m[1].replace(/\s+/g, " ").trim().split(" ");
    while (tokens.length > 2 && LEADING_STOPWORDS.has(tokens[0].toLowerCase().replace(/[^a-z]/g, ""))) {
      tokens.shift();
    }
    const entity = tokens.join(" ");
    if (entity && !out.includes(entity)) out.push(entity);
  }
  return out.length ? { value: out, origin: "question" } : none<string[]>([]);
}

// ─── confidence / freshness ──────────────────────────────────────────────────

/**
 * High when bindings exist and none carry `unverified: true`; Medium when the
 * answer rendered an unverified marker (or there is nothing machine-verified to
 * lean on). Never High on inference alone.
 */
export function deriveConfidence(bindings: ChatBinding[] | undefined): Derived<string> {
  const b = bindings ?? [];
  if (b.length && !b.some((x) => x?.unverified === true)) {
    return { value: "High", origin: "binding" };
  }
  return { value: "Medium", origin: "default" };
}

/** 🟢 static · 🟡 periodic · 🔴 refresh-each-period. */
export function deriveFreshness(answer: string, question: string): Derived<string> {
  if (PERFORMANCE_RE.test(`${question ?? ""} ${answer ?? ""}`)) {
    return { value: "🔴", origin: "question" };
  }
  if (FIGURE_RE.test(answer ?? "")) return { value: "🟡", origin: "chat" };
  return { value: "🟢", origin: "chat" };
}

// ─── attestation rationale ───────────────────────────────────────────────────

/**
 * The one place prefill can hurt: a default reason is a default excuse. It is
 * mitigated by requiring an explicit confirm (or an edit) before submit enables,
 * so the analyst has to LOOK at it. If that proves too soft in practice the fix
 * is to blank the field and require it.
 */
export const SUGGESTED_ATTESTATION_RATIONALE =
  "GP knowledge; not stated in an ungated corpus document.";

// ─── source names ────────────────────────────────────────────────────────────

export function sourceNamesOf(sources: ChatSource[] | undefined): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const s of sources ?? []) {
    const name = (s?.document_name ?? "").trim();
    if (!name || seen.has(name.toLowerCase())) continue;
    seen.add(name.toLowerCase());
    out.push(name);
  }
  return out;
}

// ─── the whole prefill in one call ───────────────────────────────────────────

export type NuancePrefill = {
  fund: string;
  vehicle_label: Derived<string>;
  question: string;
  answer_body: string;
  bindings: ChatBinding[];
  sources: string[];
  as_of: Derived<string>;
  investors: Derived<string[]>;
  confidence: Derived<string>;
  freshness: Derived<string>;
  category: Derived<string>;
  attestation_rationale: string;
  /** True when the gate will REQUIRE an as_of on submit (the answer states a figure). */
  as_of_required: boolean;
};

export function buildPrefill(args: {
  fund: string;
  fundName?: string | null;
  question: string;
  answerBody: string;
  bindings?: ChatBinding[];
  sources?: ChatSource[];
  /** Category of the nearest existing golden, when the best-effort lookup landed. */
  nearestCategory?: string | null;
}): NuancePrefill {
  const sources = sourceNamesOf(args.sources);
  const bindings = args.bindings ?? [];
  const label = (args.fundName ?? "").trim();
  return {
    fund: args.fund,
    vehicle_label: label
      ? { value: label, origin: "catalog" }
      : { value: args.fund, origin: "default" },
    question: args.question,
    answer_body: args.answerBody,
    bindings,
    sources,
    as_of: deriveAsOf(bindings, sources),
    investors: extractInvestorEntities(args.question),
    confidence: deriveConfidence(bindings),
    freshness: deriveFreshness(args.answerBody, args.question),
    category: args.nearestCategory
      ? { value: args.nearestCategory, origin: "nearest-golden" }
      : none(""),
    attestation_rationale: SUGGESTED_ATTESTATION_RATIONALE,
    as_of_required: FIGURE_RE.test(args.answerBody ?? ""),
  };
}

export const ORIGIN_LABEL: Record<PrefillOrigin, string> = {
  binding: "from a verified binding on this answer",
  "document-name": "parsed from a cited document name",
  question: "extracted from the question",
  chat: "derived from the answer text",
  catalog: "from the entitled-funds catalog",
  "nearest-golden": "from the nearest existing golden",
  default: "default",
  none: "not derived — add it if it applies",
};
